import { Injectable } from '@angular/core';
import {
  HttpRequest, HttpHandler, HttpEvent, HttpInterceptor as NgHttpInterceptor,
  HttpResponse, HttpErrorResponse
} from '@angular/common/http';
import { Observable, throwError, EMPTY } from 'rxjs';
import { tap, catchError } from 'rxjs/operators';
import { Router } from '@angular/router';
import { SessionModalService } from './session-modal.service';
import { AuthService } from 'src/app/service/auth.service';

@Injectable()
export class HttpInterceptor implements NgHttpInterceptor {
  private modalShown = false;

  constructor(
    private router: Router,
    private sessionModal: SessionModalService,
    private auth: AuthService
  ) {}

  private containsSesionKeyword(message?: string): boolean {
    if (!message) return false;
    return message.toString().toLowerCase().includes('sesión') || message.toString().toLowerCase().includes('sesion');
  }

  private showInfoModal(message: string) {
    if (this.modalShown) return;
    this.modalShown = true;
    this.sessionModal.show(message || 'Información del sistema.');
    // liberamos el flag tras un timeout (o idealmente cuando el servicio modal llame a hide())
    setTimeout(() => { this.modalShown = false; }, 2000);
  }

  private handleSessionExpired(message: string) {
    if (this.modalShown) return;
    this.modalShown = true;
    this.sessionModal.show(message || 'Su sesión ha caducado.');
    setTimeout(() => {
      this.auth.logout(true);
      this.modalShown = false;
    }, 800);
  }

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // bloquear peticiones cuando estamos en logout intencional
    if (localStorage.getItem('loggingOut') === '1' || this.auth?.isLoggingOut) {
      return EMPTY;
    }

    return next.handle(request).pipe(
      tap((event: HttpEvent<any>) => {
        if (!(event instanceof HttpResponse)) return;
        const body = event.body;
        if (!body) return;

        // intentamos obtener el objeto estado en distintos lugares
        const estadoCandidates = [
          body?.estado,
          body?.servicio?.estado,
          (typeof body === 'string') ? (() => {
            try { return JSON.parse(body)?.estado; } catch { return null; }
          })() : null
        ];
        const estado = estadoCandidates.find(e => e != null) as any;
        if (!estado) return;

        const num = estado.numeroError;
        const msg = (estado.mensajeError || estado.mensaje || '').toString();

        if (num === 999) {
          // si el mensaje contiene "sesión" => consideramos esto fatal y hacemos logout
          if (this.containsSesionKeyword(msg)) {
            console.warn('[Interceptor] 999 -> mensaje contiene "sesión": forzando logout', msg, request.url);
            this.handleSessionExpired(msg);
          } else {
            // solo mostrar modal informativo, no cerrar sesión
            console.warn('[Interceptor] 999 no-fatal -> sólo modal', msg, request.url);
            this.showInfoModal(msg || 'Operación no permitida');
          }
        } else if (msg && msg.toLowerCase().includes('reemplazada')) {
          // si el texto contiene 'reemplazada' tratamos como expiración de sesión
          this.handleSessionExpired(msg);
        }
      }),
      catchError((err: any) => {
        // si ya estamos en logout, no hacer nada especial
        if (localStorage.getItem('loggingOut') === '1' || this.auth?.isLoggingOut) {
          return throwError(() => err);
        }

        if (err instanceof HttpErrorResponse) {
          const estadoErr = err.error?.estado || err.error?.servicio?.estado;
          const msgErr = (estadoErr?.mensajeError || err.error?.message || err.message || '').toString();
          const numErr = estadoErr?.numeroError;

          if (numErr === 999) {
            if (this.containsSesionKeyword(msgErr)) {
              console.warn('[Interceptor] HTTP 999 fatal -> logout', msgErr, request.url);
              this.handleSessionExpired(msgErr);
              return throwError(() => err);
            } else {
              console.warn('[Interceptor] HTTP 999 non-fatal -> modal', msgErr, request.url);
              this.showInfoModal(msgErr || 'Operación no permitida');
              return throwError(() => err);
            }
          }

          if (err.status === 401 || err.status === 403) {
            this.handleSessionExpired('Su sesión ha caducado. Por favor inicie sesión nuevamente.');
            return throwError(() => err);
          }
        }

        return throwError(() => err);
      })
    );
  }
}
